import { StarsButton, Toggle, ToggleGroup, Translation } from '@ps/web-shared-components';
import { createForm } from '@ps/web-shared-ui';
import type { FormApi, SubmissionErrors } from 'final-form';
import React from 'react';
import { useDispatch } from 'react-redux';

import ButtonGroup from '@swf/components/ButtonGroup/ButtonGroup';
import Body from '@swf/components/StarsModal/components/Body';
import CloseButton from '@swf/components/StarsModal/components/CloseButton';
import Header from '@swf/components/StarsModal/components/Header/Header';
import { StatusCode } from '@swf/core/enums/statusCode';
import useRequest from '@swf/core/hooks/useRequest';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import type { Dispatch } from '@swf/core/store';

import type {
  UpdateLimitsPayload,
  UserLimitsResponse
} from '../ResponsibleGaming/RestrictDepositLimitExtended/RestrictDepositLimitExtended';

import {
  TXTCLI_Cancel,
  TXTCLI_Do_you_fund_your_gaming_activi_ELL,
  TXTCLI_Do_you_gamble_to_escape_from_a_ELL,
  TXTCLI_Do_you_often_find_yourself_rem_ELL,
  TXTCLI_Do_you_think_you_have_a_proble_ELL,
  TXTCLI_Do_you_try_to_controlX_cut_bac_ELL,
  TXTCLI_Does_attempting_to_cut_back_or_ELL,
  TXTCLI_Have_you_ever_gambled_more_mon_ELL,
  TXTCLI_Have_you_ever_gone_to_someone__ELL,
  TXTCLI_Have_you_ever_lost_time_from_w_ELL,
  TXTCLI_If_you_agree_with_any_of_them__ELL,
  TXTCLI_In_accordance_with_Spanish_law_ELL,
  TXTCLI_No,
  TXTCLI_Please_read_and_respond_to_the_ELL,
  TXTCLI_Submit,
  TXTCLI_Test_of_Responsible_Gaming_and_ELL,
  TXTCLI_Thank_you_for_requesting_a_dep_ELL,
  TXTCLI_When_you_bet_moneyX_do_you_pla_ELL,
  TXTCLI_Yes
} from './StarsQuiz.messages.json';
import styles from './StarsQuiz.scss';

interface FormValues {
  answer0: QuizAnswer;
  answer1: QuizAnswer;
  answer2: QuizAnswer;
  answer3: QuizAnswer;
  answer4: QuizAnswer;
  answer5: QuizAnswer;
  answer6: QuizAnswer;
  answer7: QuizAnswer;
  answer8: QuizAnswer;
  answer9: QuizAnswer;
}

export enum QuizResult {
  Pass = 1,
  Fail = 2
}

enum QuizAnswer {
  YES = '0',
  NO = '1'
}

const options = [
  { label: TXTCLI_Yes, value: QuizAnswer.YES },
  { label: TXTCLI_No, value: QuizAnswer.NO }
];

const questions = [
  TXTCLI_Do_you_often_find_yourself_rem_ELL,
  TXTCLI_Have_you_ever_gambled_more_mon_ELL,
  TXTCLI_Do_you_try_to_controlX_cut_bac_ELL,
  TXTCLI_Does_attempting_to_cut_back_or_ELL,
  TXTCLI_Do_you_gamble_to_escape_from_a_ELL,
  TXTCLI_When_you_bet_moneyX_do_you_pla_ELL,
  TXTCLI_Do_you_think_you_have_a_proble_ELL,
  TXTCLI_Do_you_fund_your_gaming_activi_ELL,
  TXTCLI_Have_you_ever_lost_time_from_w_ELL,
  TXTCLI_Have_you_ever_gone_to_someone__ELL
];

interface Result {
  quizResult: number;
  answers: number;
}

interface SearchParams {
  pathToRedirect?: string;
  updateLimitsPayload: UpdateLimitsPayload;
}

const Form = createForm<FormValues>();

const processSubmittedAnswers = (submittedAnswers: FormValues): Result => {
  const answers: number = Object.values(submittedAnswers).reduce((memo, answer, index) => {
    if (parseInt(answer, 10) === 0) {
      memo += Math.pow(2, index);
    }
    return memo;
  }, 0);

  const quizResult = answers > 0 ? QuizResult.Fail : QuizResult.Pass;

  return { answers, quizResult };
};

const StarsQuiz: React.FC = () => {
  const request = useRequest();
  const { updateLimitsPayload, pathToRedirect } = useSearchParams<SearchParams>();

  const { modal } = useDispatch<Dispatch>();

  const closeQuiz = (
    limitResponse?: UserLimitsResponse | null,
    wrongPassword?: boolean,
    increaseNotAllowed?: boolean
  ) => {
    let attemptedLimits:
      | { dailyLimit?: number; weeklyLimit?: number; monthlyLimit?: number }
      | undefined;

    if (wrongPassword) {
      const { dailyLimit, weeklyLimit, monthlyLimit } = updateLimitsPayload;

      attemptedLimits = {
        dailyLimit: dailyLimit && dailyLimit / 100,
        weeklyLimit: weeklyLimit && weeklyLimit / 100,
        monthlyLimit: monthlyLimit && monthlyLimit / 100
      };
    }

    if (pathToRedirect) {
      modal.enqueue({
        path: pathToRedirect,
        locationState: { limitResponse, attemptedLimits, wrongPassword, increaseNotAllowed }
      });
    }

    modal.close('/quiz');
  };

  const handleSubmit = (
    values: FormValues,
    _: FormApi<FormValues>,
    callback?: (errors?: SubmissionErrors) => void
  ) =>
    request({
      service: 'responsiblegaming.updateuserlimit',
      body: {
        ...updateLimitsPayload,
        ...processSubmittedAnswers(values)
      }
    }).subscribe(
      ({ body: limitResponse }) => {
        callback?.();
        closeQuiz(limitResponse);
      },
      ({ headers: { statusCode } }) => {
        switch (statusCode) {
          case StatusCode.WRONG_PWD:
            callback?.();
            closeQuiz(null, true);
            break;
          case StatusCode.NOT_ALLOWED:
            closeQuiz(null, false, true);
            break;
        }
      }
    );

  return (
    <>
      <Header>
        <Translation {...TXTCLI_Test_of_Responsible_Gaming_and_ELL} />
        <CloseButton />
      </Header>
      <Body>
        <Form
          onSubmit={handleSubmit}
          render={({ values, submitting }) => (
            <>
              <p>
                <Translation {...TXTCLI_Thank_you_for_requesting_a_dep_ELL} />
                <Translation {...TXTCLI_In_accordance_with_Spanish_law_ELL} />
                <Translation {...TXTCLI_Please_read_and_respond_to_the_ELL} />
                <Translation {...TXTCLI_If_you_agree_with_any_of_them__ELL} />
              </p>
              <table className={styles.table}>
                <tbody>
                  {questions.map((question, index) => (
                    <tr key={`question${index}`}>
                      <Translation tagName="td" {...question} />
                      <td className={styles.toggleCell}>
                        <ToggleGroup
                          id={`answer${index}`}
                          type="radio"
                          label=""
                          className={styles.toggleGroup}
                        >
                          {options.map((option) => (
                            <Toggle
                              id={`answer${index}${option.value}`}
                              key={option.value}
                              name={`answer${index}`}
                              label={option.label}
                              value={option.value}
                              type="radio"
                              className={styles.toggle}
                            />
                          ))}
                        </ToggleGroup>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ButtonGroup inline className={styles.buttonGroup}>
                <StarsButton
                  variant="secondary"
                  onClick={() => closeQuiz()}
                  translation={TXTCLI_Cancel}
                />
                <StarsButton
                  type="submit"
                  disabled={Object.keys(values).length < questions.length}
                  loading={submitting}
                  translation={TXTCLI_Submit}
                />
              </ButtonGroup>
            </>
          )}
        />
      </Body>
    </>
  );
};

export default StarsQuiz;
